import React from 'react';
import styled from 'styled-components';
import { Button, IconButton, Menu, MenuItem, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FolderIcon from '@mui/icons-material/Folder';
import FolderOffOutlinedIcon from '@mui/icons-material/FolderOffOutlined';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useTranslation } from 'react-i18next';
import {
  LcaFolderFilter,
  LcaProductFolderOutSchema,
  LcaProductFolderSummaryOutSchema,
} from '../types';
import { flattenFolderTree, getFolderPath } from './folderTree';

// indentation added per nesting level of the folder tree
const LEVEL_INDENT_PX = 14;

const Sidebar = styled.aside`
  flex: none;
  width: 280px;
  align-self: flex-start;
  padding: 20px 14px;
  border-radius: 16px;
  border: 3px solid var(--Stroke, #dadada);
  background: var(--White, #fff);
  box-shadow: 0 2px 2px rgba(0, 0, 0, 0.25);
`;

const SidebarHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 0 4px 12px 12px;
`;

const Row = styled.div<{ $active: boolean }>`
  display: flex;
  align-items: center;
  border-radius: 8px;
  background: ${({ $active }) =>
    $active ? 'var(--brand-color-light, #fff7e8)' : 'transparent'};

  &:hover {
    background: var(--brand-color-light, #fff7e8);
  }

  .MuiIconButton-root {
    visibility: hidden;
  }

  &:hover .MuiIconButton-root {
    visibility: visible;
  }
`;

const RowButton = styled.button<{ $active: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  min-width: 0;
  padding: 10px 4px 10px 12px;
  border: none;
  background: none;
  cursor: pointer;
  text-align: left;
  font-family: inherit;
  font-size: 15px;
  letter-spacing: 0.02em;
  color: ${({ $active }) =>
    $active ? 'var(--brand-color-main, #7e4308)' : '#281302'};
  font-weight: ${({ $active }) => ($active ? 600 : 500)};

  svg {
    flex: none;
    color: ${({ $active }) =>
      $active ? 'var(--brand-color-main, #7e4308)' : '#606462'};
  }
`;

const ExpandToggle = styled.button`
  flex: none;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border: none;
  border-radius: 4px;
  background: none;
  color: #606462;
  cursor: pointer;

  &:hover {
    background: rgba(0, 0, 0, 0.06);
  }
`;

const ExpandSpacer = styled.span`
  flex: none;
  width: 24px;
`;

const RowLabel = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const RowCount = styled.span`
  margin-left: auto;
  padding-left: 8px;
  font-size: 13px;
  font-weight: 400;
  color: #6e6e6e;
  font-variant-numeric: tabular-nums;
`;

const withExpanded = (current: Set<number>, folderIds: number[]) =>
  folderIds.every((folderId) => current.has(folderId))
    ? current
    : new Set([...Array.from(current), ...folderIds]);

interface FoldersSidebarProps {
  folders: LcaProductFolderOutSchema[];
  summary?: LcaProductFolderSummaryOutSchema;
  selectedFolder: LcaFolderFilter;
  canWrite: boolean;
  // eslint-disable-next-line no-unused-vars
  onSelect: (folder: LcaFolderFilter) => void;
  onCreate: () => void;
  // eslint-disable-next-line no-unused-vars
  onCreateSubfolder: (parent: LcaProductFolderOutSchema) => void;
  // eslint-disable-next-line no-unused-vars
  onMove: (folder: LcaProductFolderOutSchema) => void;
  // eslint-disable-next-line no-unused-vars
  onRename: (folder: LcaProductFolderOutSchema) => void;
  // eslint-disable-next-line no-unused-vars
  onDelete: (folder: LcaProductFolderOutSchema) => void;
}

export default function FoldersSidebar({
  folders,
  summary,
  selectedFolder,
  canWrite,
  onSelect,
  onCreate,
  onCreateSubfolder,
  onMove,
  onRename,
  onDelete,
}: FoldersSidebarProps) {
  const { t } = useTranslation('lca');
  const [menuAnchorEl, setMenuAnchorEl] = React.useState<null | HTMLElement>(
    null
  );
  const [menuFolder, setMenuFolder] =
    React.useState<LcaProductFolderOutSchema | null>(null);
  const [expandedIds, setExpandedIds] = React.useState<Set<number>>(
    () => new Set()
  );

  const expand = (folderIds: number[]) =>
    setExpandedIds((current) => withExpanded(current, folderIds));

  const toggleExpanded = (folderId: number) =>
    setExpandedIds((current) => {
      const next = new Set(Array.from(current));
      if (next.has(folderId)) next.delete(folderId);
      else next.add(folderId);
      return next;
    });

  // a folder opened from the path above the list must be visible in the tree
  React.useEffect(() => {
    if (typeof selectedFolder !== 'number') return;
    const ancestorIds = getFolderPath(folders, selectedFolder)
      .slice(0, -1)
      .map((ancestor) => ancestor.id);
    setExpandedIds((current) => withExpanded(current, ancestorIds));
  }, [selectedFolder, folders]);

  const rows = flattenFolderTree(folders, (folderId) =>
    expandedIds.has(folderId)
  );

  const openMenu = (
    event: React.MouseEvent<HTMLElement>,
    folder: LcaProductFolderOutSchema
  ) => {
    setMenuAnchorEl(event.currentTarget);
    setMenuFolder(folder);
  };

  const closeMenu = () => {
    setMenuAnchorEl(null);
    setMenuFolder(null);
  };

  const runFolderAction = (
    // eslint-disable-next-line no-unused-vars
    action: (folder: LcaProductFolderOutSchema) => void
  ) => {
    if (menuFolder) action(menuFolder);
    closeMenu();
  };

  return (
    <Sidebar>
      <SidebarHeader>
        <Typography variant="h6">{t('folders.title')}</Typography>
        {canWrite && (
          <Button
            variant="text"
            size="small"
            onClick={onCreate}
            sx={{ px: '8px', minWidth: 0, whiteSpace: 'nowrap' }}
          >
            <AddIcon fontSize="small" sx={{ mr: '4px' }} />
            {t('folders.newFolder')}
          </Button>
        )}
      </SidebarHeader>

      <Row $active={selectedFolder === undefined}>
        <RowButton
          type="button"
          $active={selectedFolder === undefined}
          onClick={() => onSelect(undefined)}
        >
          <FolderOutlinedIcon fontSize="small" />
          <RowLabel>{t('folders.allProducts')}</RowLabel>
          {summary && <RowCount>{summary.total}</RowCount>}
        </RowButton>
      </Row>

      {rows.map(({ folder, depth, hasChildren }) => {
        const isActive = selectedFolder === folder.id;
        const isExpanded = expandedIds.has(folder.id);
        return (
          <Row
            key={folder.id}
            $active={isActive}
            style={{ paddingLeft: `${4 + depth * LEVEL_INDENT_PX}px` }}
          >
            {hasChildren ? (
              <ExpandToggle
                type="button"
                aria-expanded={isExpanded}
                aria-label={t(
                  isExpanded
                    ? 'folders.actions.collapse'
                    : 'folders.actions.expand'
                )}
                onClick={() => toggleExpanded(folder.id)}
              >
                {isExpanded ? (
                  <ExpandMoreIcon fontSize="small" />
                ) : (
                  <ChevronRightIcon fontSize="small" />
                )}
              </ExpandToggle>
            ) : (
              <ExpandSpacer />
            )}
            <RowButton
              type="button"
              $active={isActive}
              onClick={() => onSelect(folder.id)}
              title={folder.name}
              style={{ paddingLeft: '4px' }}
            >
              <FolderIcon fontSize="small" />
              <RowLabel>{folder.name}</RowLabel>
              <RowCount>{folder.total_product_count}</RowCount>
            </RowButton>
            {canWrite && (
              <IconButton
                size="small"
                aria-label={folder.name}
                onClick={(event) => openMenu(event, folder)}
              >
                <MoreVertIcon fontSize="small" />
              </IconButton>
            )}
          </Row>
        );
      })}

      <Row $active={selectedFolder === null}>
        <RowButton
          type="button"
          $active={selectedFolder === null}
          onClick={() => onSelect(null)}
        >
          <FolderOffOutlinedIcon fontSize="small" />
          <RowLabel>{t('folders.withoutFolder')}</RowLabel>
          {summary && <RowCount>{summary.without_folder}</RowCount>}
        </RowButton>
      </Row>

      {!folders.length && (
        <Typography
          variant="captionText"
          color="text.secondary"
          sx={{ display: 'block', p: '12px' }}
        >
          {t('folders.empty')}
        </Typography>
      )}

      <Menu anchorEl={menuAnchorEl} open={!!menuAnchorEl} onClose={closeMenu}>
        <MenuItem
          onClick={() =>
            runFolderAction((parent) => {
              // keep the new sub-folder in sight once it is created
              expand([parent.id]);
              onCreateSubfolder(parent);
            })
          }
        >
          {t('folders.actions.newSubfolder')}
        </MenuItem>
        <MenuItem onClick={() => runFolderAction(onMove)}>
          {t('folders.actions.move')}
        </MenuItem>
        <MenuItem onClick={() => runFolderAction(onRename)}>
          {t('folders.actions.rename')}
        </MenuItem>
        <MenuItem onClick={() => runFolderAction(onDelete)}>
          {t('folders.actions.delete')}
        </MenuItem>
      </Menu>
    </Sidebar>
  );
}
